import React, { useEffect, useState, useMemo, useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { devices } from "../../styles";
import placeholder from "../../assets/test.png";
import { useOffers } from "../../context/offerContext";
import { useResponses } from "../../context/responseContext";
import { PrimaryButton, SecondaryButton } from "../Button";
import ArrowLeft from "../Button/Icons/ArrowLeft";
import ArrowRight from "../Button/Icons/ArrowRight";
import PrescriptionInfoForm from "./PrescriptionInfoForm";
import {
  questions,
  RESPONSE_SECTIONS,
} from "../../routes/Questionnaire/questions";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import FinishedModal from "../FinishedModal";

export default function ProductInfo({ openModal, returnToStart, goToPreviousStep, modalOpen }) {
  const { selected, selectedProduct } = useOffers();
  const { responses } = useResponses();
  const [finishedModalOpen, setFinishedModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showError, setShowError] = useState(false);
  const prescriptionFormRef = useRef(null);

  const compressionClassKey = responses[RESPONSE_SECTIONS.COMPRESSION_CLASS];
  const compressionClass =
    compressionClassKey.length > 0
      ? questions[2].answers.find((e) => e.key === compressionClassKey).heading
          ?.replace(/&nbsp;/g, ' ')
      : null;

  const leftProduct = selectedProduct["left"];
  const rightProduct = selectedProduct["right"];

  // fade in content on initial load
  useEffect(() => {
    gsap.fromTo(
      [...document.querySelectorAll("#content")],
      { opacity: 0 },
      {
        opacity: 1,
        duration: 1,
      }
    );
  }, []);

  const fadeOutAnimation = () => {
    gsap.fromTo(
      [...document.querySelectorAll("#content")],
      { opacity: 1 },
      {
        opacity: 1,
        duration: 0.5,
        onComplete: () => {
          returnToStart();

          // fade back in first question content (will be 0 opacity by default)
          gsap.fromTo(
            [...document.querySelectorAll("#content")],
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.5,
            }
          );
        },
      }
    );
  };

  const sendEmailButtonClick = () => {
    // if form not validated show error, otherwise open the modal
    if (isError) {
      setShowError(true);
    } else {
      openModal();
    }
  };

  const isFullWidthImage =
    selected.name === "Mediven Plus" || selected.name === "Duomed soft";

  return selected ? (
    <Wrapper id="content">
      
      <Background>
        <Content>
          <InfoContainer>
            
            <MainImage>
              {isFullWidthImage ? (
                <FullWidthImage
                  alt="Selected garment"
                  src={selected ? selected.img : placeholder}
                />
              ) : (
                <img
                  alt="Selected garment"
                  src={selected ? selected.img : placeholder}
                />
              )}
            </MainImage>
            <LegDescription>
              {selected && (
                <>
                <h2>Product specifications</h2>
                  <h3>{selected.displayName}</h3>
                  <p  style={{fontSize: ".75rem"}}>
                    <b>Compression Class:</b> {compressionClass}
                     {selected && responses[RESPONSE_SECTIONS.TOE_TYPE] && (
                      <>
                        <br />
                        <b>Toe style: </b>
                        {selected &&
                          responses[RESPONSE_SECTIONS.TOE_TYPE] &&
                          capitalizeFirstLetter(
                            responses[RESPONSE_SECTIONS.TOE_TYPE]
                          )}
                        
                      </>
                    )}
                    {selected && responses[RESPONSE_SECTIONS.COLOR] && (
                      <>
                        <br />
                        <b>Colour: </b>
                         {selected &&
                            responses[RESPONSE_SECTIONS.COLOR] &&
                            capitalizeFirstLetter(
                              responses[RESPONSE_SECTIONS.COLOR]
                            )}
                      </>
                    )}
                  </p>
                  <hr />
                </>
              )}
            </LegDescription>
          </InfoContainer>
         
            {leftProduct &&
            (
                <LegInfo
                  leg="Left"
                  productCode={leftProduct ? leftProduct.code : null}
                  pipCode={leftProduct ? leftProduct.pip : null}
                  size={leftProduct ? leftProduct.size : null}
                />
            )}
            {leftProduct && rightProduct &&
                <hr />
            }
            {rightProduct &&
            (<LegInfo
              leg="Right"
              productCode={rightProduct ? rightProduct.code : null}
              pipCode={rightProduct ? rightProduct.pip : null}
              size={rightProduct ? rightProduct.size : null}
            />
            )}
          
        </Content>
      </Background>
      <div ref={prescriptionFormRef}>
        <PrescriptionInfoForm
          showError={showError}
          setShowError={setShowError}
          setIsError={setIsError}
        />
      </div>
      <FooterContent>
        <ButtonContainer>
          <SecondaryButton
            onClick={() => {
              goToPreviousStep();
            }}
          >
            <ArrowLeft />
            Back
          </SecondaryButton>
          <PrimaryButton
            disabled={isError}
            onClick={() => {
              sendEmailButtonClick();
            }}
          >
            Send email
          </PrimaryButton>
        </ButtonContainer>
      </FooterContent>
      <FinishedModal
        isOpen={finishedModalOpen}
        close={() => setFinishedModalOpen(false)}
        reset={fadeOutAnimation}
      />
    </Wrapper>
  ) : null;
}

const LegInfo = ({ leg, productCode, pipCode, color, size }) => {
  return (
    <>
        <Icon>{leg.charAt(0)}</Icon>
        <p style={{
          fontSize: ".75rem", 
          display: "inline-block",
          margin: '16px 0 8px',
          lineHeight: '22px',
          }}>
          <b>{leg} Leg:</b>
          </p>


        <p style={{fontSize: ".75rem"}}>
          <b>Order code:</b> {productCode ? productCode : "-"}

          <br />
          <b>PIP code:</b> {pipCode ? pipCode : "-"}

          <br />
          <b>Size:</b> {size ? size : "-"}
        </p>

    </>
  );
};

const Wrapper = styled.div`
  color: ${(props) => props.theme.colorSecondary};
`;
const Background = styled.div`
  color: #000000;
  position: relative;
`;

const Content = styled.div`
  background-color: #fcfbfa;
  margin: 32px 16px;
  border-radius: 8px;
  border: 1px solid #ECEAEA;
  padding: 16px;

  h2{
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; 
    margin: 1rem 0;
  }
  h3{
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 1rem;
  }
  h4{
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
  }

  hr{
  height:1px;
  border:none;
  background:#C0BEBD;
  }

  p {
    margin-bottom: 16px;
    line-height: 22px;
  }

  @media ${devices.tablet} {
    padding: 16px;
    max-width: 756px;
    width: calc(100% - 64px);
    margin: 72px auto 40px;    
  }

  @media ${devices.desktop} {
    max-width: 756px;
    margin: inherit auto;
    box-sizing: border-box;
  }
`;

const InfoContainer = styled.div`
  
`;

const Image = styled.div`
  background: #e9edf0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  border-radius: 6px;
  img {
    width: 103px;
  }
`;

const MainImage = styled(Image)`
  position: relative;

  @media ${devices.tablet} {
    img {
      width: 200px;
    }
  }
  @media ${devices.desktop} {
    img {
      width: 211px;
    }
  }
`;

const Icon = styled.div`
  border-radius: 50%;
  background: #E20074;
  width: 14px;
  height: 14px;
  color: black;
  font-size: 16px;
  font-weight: 400;
  font-size:8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px 0 0;
  display: inline-block;
  text-align: center;
  line-height: 14px;
  color: #FFFFFF;
`;
const FooterContent = styled.div`
  height: 80px;
  padding: 24px 16px 16px;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 1px 2px rgba(60, 64, 67, 0.3),
    0px 2px 6px 2px rgba(60, 64, 67, 0.15);
  z-index: 1;
  position:fixed;
  bottom:0;
  background:#fff;
  width:100vw;
  text-align:center;

  
  @media ${devices.tablet} {
    
    flex-direction: row-reverse;
    align-items: center;
    justify-content: center;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 0 16px;
  max-width: 756px;

  button + button {
    margin-left: auto;
  }

  @media ${devices.tablet} {
    width:756px;
    max-width:100%;
    margin-bottom: 0px;
  }
`;


const LegDescription = styled.div`
 
`;

const FullWidthImage = styled.img`
  max-height: 123px;
  width: auto !important;

  @media ${devices.tablet} {
    max-height: 240px;
  }
  @media ${devices.desktop} {
    max-height: 250px;
  }
`;